import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { Receipt, Download, Eye, X, Filter, Calendar, User, DollarSign, FileText, Image, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils/helpers';

const ReceiptManagement = ({ clubId, players, games, tournaments = [], onBack }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [filter, setFilter] = useState({
    status: 'all',
    receiptStatus: 'all', // all, with-receipt, no-receipt
    player: '',
    tournament: '',
    dateFrom: '',
    dateTo: '',
    search: '',
    docType: 'all' // all, expense, payment
  });
  const [viewMode, setViewMode] = useState('grid'); // grid or list

  useEffect(() => {
    if (clubId) {
      loadDocuments();
    }
  }, [clubId]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const allDocuments = [];

      // Load expenses from top-level expenses collection
      const expensesRef = collection(db, 'expenses');
      const expensesQuery = query(expensesRef, where('clubId', '==', clubId));
      const expensesSnapshot = await getDocs(expensesQuery);

      expensesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.receiptURL || data.receiptUrl) {
          allDocuments.push({
            id: doc.id,
            type: 'expense',
            description: data.description || data.type || 'Expense',
            amount: data.amount,
            receiptUrl: data.receiptURL || data.receiptUrl,
            createdAt: data.createdAt,
            submittedBy: data.createdByName || data.addedByPlayerName || 'Unknown',
            submitterId: data.addedByPlayerId || data.createdBy,
            approvalStatus: data.approvalStatus,
            gameId: data.gameId,
            gameName: data.gameName,
            tournamentId: data.tournamentId
          });
        }
      });

      // Load payment receipts from walletTransactions
      const walletRef = collection(db, 'walletTransactions');
      const walletQuery = query(walletRef, where('clubId', '==', clubId));
      const walletSnapshot = await getDocs(walletQuery);

      walletSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.receiptURL || data.receiptUrl) {
          allDocuments.push({
            id: doc.id,
            type: 'payment',
            description: data.description || 'Payment',
            amount: Math.abs(data.amount),
            receiptUrl: data.receiptURL || data.receiptUrl,
            createdAt: data.createdAt || data.paymentDate,
            submittedBy: data.playerName || 'Unknown',
            submitterId: data.userId,
            approvalStatus: data.status,
            paymentMethod: data.paymentMethod,
            transactionId: data.transactionId
          });
        }
      });

      // Load payment submission receipts
      const submissionsRef = collection(db, 'paymentSubmissions');
      const submissionsQuery = query(submissionsRef, where('clubId', '==', clubId));
      const submissionsSnapshot = await getDocs(submissionsQuery);

      submissionsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.receiptURL || data.receiptUrl) {
          // Check if we already have this from walletTransactions
          const existingPayment = allDocuments.find(d => d.type === 'payment' && d.submitterId === data.playerId);
          if (!existingPayment) {
            allDocuments.push({
              id: doc.id,
              type: 'payment',
              description: `Payment via ${data.paymentMethod || 'Unknown'}`,
              amount: data.amount,
              receiptUrl: data.receiptURL || data.receiptUrl,
              createdAt: data.createdAt || data.paymentDate,
              submittedBy: data.playerName || 'Unknown',
              submitterId: data.playerId,
              approvalStatus: data.status,
              paymentMethod: data.paymentMethod,
              transactionId: data.transactionId
            });
          }
        }
      });

      // Sort by date descending
      allDocuments.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      console.log('📄 Loaded documents with receipts:', allDocuments.length);
      setDocuments(allDocuments);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get filtered documents
  const getFilteredDocuments = () => {
    let filtered = [...documents];

    if (filter.status !== 'all') {
      filtered = filtered.filter(doc => doc.approvalStatus === filter.status);
    }

    if (filter.docType && filter.docType !== 'all') {
      filtered = filtered.filter(doc => doc.type === filter.docType);
    }

    if (filter.player) {
      filtered = filtered.filter(doc =>
        doc.submitterId === filter.player ||
        doc.submittedBy?.toLowerCase().includes(filter.player.toLowerCase())
      );
    }

    if (filter.tournament) {
      const tournamentGameIds = (games || [])
        .filter(g => g.tournamentId === filter.tournament)
        .map(g => g.id);
      filtered = filtered.filter(doc => tournamentGameIds.includes(doc.gameId) || doc.tournamentId === filter.tournament);
    }

    if (filter.dateFrom) {
      filtered = filtered.filter(doc => doc.createdAt >= filter.dateFrom);
    }

    if (filter.dateTo) {
      filtered = filtered.filter(doc => doc.createdAt <= filter.dateTo + 'T23:59:59');
    }

    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.description?.toLowerCase().includes(searchLower) ||
        doc.submittedBy?.toLowerCase().includes(searchLower) ||
        doc.paymentMethod?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  };

  // Get submitter name
  const getSubmitterName = (doc) => {
    return doc.submittedBy || 'Unknown';
  };

  // Get game name
  const getGameName = (gameId) => {
    if (!gameId) return null;
    const game = (games || []).find(g => g.id === gameId);
    return game?.name || 'Unknown Game';
  };

  // Check if URL is an image
  const isImageUrl = (url) => {
    if (!url) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
    const lowerUrl = url.toLowerCase();
    return imageExtensions.some(ext => lowerUrl.includes(ext)) || lowerUrl.includes('image');
  };

  const filteredDocuments = getFilteredDocuments();

  // Unique submitters for filter dropdown
  const uniqueSubmitters = [...new Set(documents.map(doc => doc.submittedBy))].filter(Boolean).sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="text-gray-600 hover:text-gray-800 flex items-center gap-2 mb-4"
        >
          ← Back to Overview
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Receipt className="text-green-600" />
              Document Center
            </h1>
            <p className="text-gray-600 mt-1">
              {filteredDocuments.length} receipt{filteredDocuments.length !== 1 ? 's' : ''} found
            </p>
          </div>

          {/* View Toggle */}
          <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'grid' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              List
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search by description, type..."
                value={filter.search}
                onChange={(e) => setFilter(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="w-full lg:w-40">
            <select
              value={filter.status}
              onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="all">All Status</option>
              <option value="approved">✅ Approved</option>
              <option value="pending">⏳ Pending</option>
              <option value="rejected">❌ Rejected</option>
            </select>
          </div>

          {/* Receipt Filter */}
          <div className="w-full lg:w-44">
            <select
              value={filter.docType || 'all'}
              onChange={(e) => setFilter(prev => ({ ...prev, docType: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="all">All Documents</option>
              <option value="expense">📄 Expense Receipts</option>
              <option value="payment">💳 Payment Receipts</option>
            </select>
          </div>

          {/* Submitter Filter */}
          <div className="w-full lg:w-48">
            <select
              value={filter.player}
              onChange={(e) => setFilter(prev => ({ ...prev, player: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Submitters</option>
              {uniqueSubmitters.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          {/* Tournament Filter */}
          {tournaments.length > 0 && (
            <div className="w-full lg:w-48">
              <select
                value={filter.tournament}
                onChange={(e) => setFilter(prev => ({ ...prev, tournament: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">All Tournaments</option>
                {tournaments.map(t => (
                  <option key={t.id} value={t.id}>🏆 {t.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Date Range */}
          <div className="flex gap-2">
            <input
              type="date"
              value={filter.dateFrom}
              onChange={(e) => setFilter(prev => ({ ...prev, dateFrom: e.target.value }))}
              className="w-full lg:w-auto px-3 py-2 border rounded-lg"
              placeholder="From"
            />
            <input
              type="date"
              value={filter.dateTo}
              onChange={(e) => setFilter(prev => ({ ...prev, dateTo: e.target.value }))}
              className="w-full lg:w-auto px-3 py-2 border rounded-lg"
              placeholder="To"
            />
          </div>

          {/* Clear Filters */}
          {(filter.status !== 'all' || filter.receiptStatus !== 'all' || filter.player || filter.tournament || filter.dateFrom || filter.dateTo || filter.search) && (
            <button
              onClick={() => setFilter({ status: 'all', receiptStatus: 'all', player: '', tournament: '', dateFrom: '', dateTo: '', search: '' })}
              className="px-3 py-2 text-gray-600 hover:text-gray-800 text-sm whitespace-nowrap"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* No Documents */}
      {filteredDocuments.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Receipt className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Documents Found</h3>
          <p className="text-gray-500">
            {documents.length === 0
              ? 'No receipts or payment documents have been uploaded yet.'
              : 'No documents match your current filters.'}
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredDocuments.map(doc => (
            <div
              key={doc.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedReceipt(doc)}
            >
              {/* Receipt Preview */}
              <div className={`aspect-[4/3] relative ${doc.receiptUrl ? 'bg-gray-100' : 'bg-gradient-to-br from-gray-200 to-gray-300'}`}>
                {doc.receiptUrl ? (
                  isImageUrl(doc.receiptUrl) ? (
                    <img
                      src={doc.receiptUrl}
                      alt="Receipt"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FileText className="w-16 h-16 text-gray-400" />
                    </div>
                  )
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-2">
                      <Receipt className="w-8 h-8 text-amber-500" />
                    </div>
                    <span className="text-sm font-medium text-gray-600">No Receipt</span>
                  </div>
                )}

                {/* Receipt Badge */}
                {!doc.receiptUrl && (
                  <div className="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                    ⚠️ Missing
                  </div>
                )}

                {/* Status Badge */}
                <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-semibold ${doc.approvalStatus === 'approved' ? 'bg-green-500 text-white' :
                    doc.approvalStatus === 'pending' ? 'bg-amber-500 text-white' :
                      'bg-red-500 text-white'
                  }`}>
                  {doc.approvalStatus === 'approved' ? '✓ Approved' :
                    doc.approvalStatus === 'pending' ? '⏳ Pending' : '✗ Rejected'}
                </div>

                {/* Type Badge */}
                <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-semibold ${doc.type === 'payment' ? 'bg-blue-500 text-white' : 'bg-purple-500 text-white'
                  }`}>
                  {doc.type === 'payment' ? '💳 Payment' : '📄 Expense'}
                </div>

                {/* Amount Overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                  <p className="text-white font-bold text-lg">{formatCurrency(doc.amount)}</p>
                </div>
              </div>

              {/* Details */}
              <div className="p-3">
                <p className="font-semibold text-gray-900 truncate">{doc.description || 'No description'}</p>
                <p className="text-sm text-gray-600 truncate">
                  {doc.type === 'payment' && doc.paymentMethod ? `via ${doc.paymentMethod}` : doc.gameName || ''}
                </p>
                <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                  <span className="truncate">{doc.submittedBy}</span>
                  <span>{doc.createdAt ? formatDate(doc.createdAt) : ''}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted By</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredDocuments.map(doc => (
                  <tr key={doc.id} className={`hover:bg-gray-50 ${!doc.receiptUrl ? 'bg-amber-50/50' : ''}`}>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedReceipt(expense)}
                        className={`w-12 h-12 rounded-lg overflow-hidden hover:ring-2 transition-all ${doc.receiptUrl ? 'bg-gray-100 hover:ring-green-500' : 'bg-amber-100 hover:ring-amber-500'
                          }`}
                      >
                        {doc.receiptUrl ? (
                          isImageUrl(doc.receiptUrl) ? (
                            <img
                              src={doc.receiptUrl}
                              alt="Receipt"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <FileText className="w-6 h-6 text-gray-400" />
                            </div>
                          )
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Receipt className="w-6 h-6 text-amber-500" />
                          </div>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${doc.type === 'payment' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                          }`}>
                          {doc.type === 'payment' ? '💳 Payment' : '📄 Expense'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 truncate max-w-xs block">
                        {doc.description || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{doc.submittedBy}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {doc.type === 'payment' && doc.paymentMethod
                          ? `via ${doc.paymentMethod}`
                          : doc.gameName || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-semibold text-green-600">{formatCurrency(doc.amount)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {doc.createdAt ? formatDate(doc.createdAt) : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${doc.approvalStatus === 'approved' ? 'bg-green-100 text-green-800' :
                          doc.approvalStatus === 'pending' ? 'bg-amber-100 text-amber-800' :
                            'bg-red-100 text-red-800'
                        }`}>
                        {doc.approvalStatus === 'approved' ? '✓ Approved' :
                          doc.approvalStatus === 'pending' ? '⏳ Pending' : '✗ Rejected'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setSelectedReceipt(doc)}
                          className="p-1.5 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded"
                          title="View Receipt"
                        >
                          <Eye size={18} />
                        </button>
                        <a
                          href={doc.receiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Download"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Download size={18} />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Receipt Detail Modal */}
      {selectedReceipt && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Receipt Details</h3>
                <p className="text-sm text-gray-500">
                  Submitted on {formatDate(selectedReceipt.createdAt)}
                </p>
              </div>
              <button
                onClick={() => setSelectedReceipt(null)}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Receipt Image/Preview */}
                <div className={`flex items-center justify-center p-4 min-h-[300px] ${selectedReceipt.receiptUrl ? 'bg-gray-900' : 'bg-gradient-to-br from-amber-100 to-orange-100'}`}>
                  {selectedReceipt.receiptUrl ? (
                    isImageUrl(selectedReceipt.receiptUrl) ? (
                      <img
                        src={selectedReceipt.receiptUrl}
                        alt="Receipt"
                        className="max-w-full max-h-[60vh] object-contain rounded-lg"
                      />
                    ) : (
                      <div className="text-center">
                        <FileText className="w-20 h-20 text-gray-500 mx-auto mb-4" />
                        <p className="text-gray-400">Document Preview</p>
                        <a
                          href={selectedReceipt.receiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100"
                        >
                          <Download size={18} />
                          Open Document
                        </a>
                      </div>
                    )
                  ) : (
                    <div className="text-center">
                      <div className="w-24 h-24 rounded-full bg-amber-200 flex items-center justify-center mx-auto mb-4">
                        <Receipt className="w-12 h-12 text-amber-600" />
                      </div>
                      <p className="text-amber-800 font-semibold text-lg">No Receipt Attached</p>
                      <p className="text-amber-600 text-sm mt-1">This expense was submitted without a receipt</p>
                    </div>
                  )}
                </div>

                {/* Expense Details */}
                <div className="p-6">
                  {/* Status Banner */}
                  <div className={`mb-6 p-3 rounded-lg ${selectedReceipt.approvalStatus === 'approved' ? 'bg-green-50 border border-green-200' :
                      selectedReceipt.approvalStatus === 'pending' ? 'bg-amber-50 border border-amber-200' :
                        'bg-red-50 border border-red-200'
                    }`}>
                    <div className="flex items-center gap-2">
                      <span className={`text-lg ${selectedReceipt.approvalStatus === 'approved' ? 'text-green-600' :
                          selectedReceipt.approvalStatus === 'pending' ? 'text-amber-600' :
                            'text-red-600'
                        }`}>
                        {selectedReceipt.approvalStatus === 'approved' ? '✓' :
                          selectedReceipt.approvalStatus === 'pending' ? '⏳' : '✗'}
                      </span>
                      <span className={`font-semibold ${selectedReceipt.approvalStatus === 'approved' ? 'text-green-800' :
                          selectedReceipt.approvalStatus === 'pending' ? 'text-amber-800' :
                            'text-red-800'
                        }`}>
                        {selectedReceipt.approvalStatus === 'approved' ? 'Approved' :
                          selectedReceipt.approvalStatus === 'pending' ? 'Pending Approval' : 'Rejected'}
                      </span>
                    </div>
                    {selectedReceipt.approvalStatus === 'approved' && selectedReceipt.approvedAt && (
                      <p className="text-sm text-green-700 mt-1">
                        Approved on {formatDate(selectedReceipt.approvedAt)}
                      </p>
                    )}
                    {selectedReceipt.approvalStatus === 'rejected' && selectedReceipt.denialReason && (
                      <p className="text-sm text-red-700 mt-1">
                        Reason: {selectedReceipt.denialReason}
                      </p>
                    )}
                  </div>

                  {/* Amount */}
                  <div className="mb-6">
                    <p className="text-sm text-gray-500 mb-1">Amount</p>
                    <p className="text-3xl font-bold text-green-600">{formatCurrency(selectedReceipt.amount)}</p>
                  </div>

                  {/* Details Grid */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Expense Type</p>
                        <p className="font-medium text-gray-900">{selectedReceipt.type || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Date Submitted</p>
                        <p className="font-medium text-gray-900">
                          {formatDate(selectedReceipt.createdAt)}
                        </p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-1">Submitted By</p>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {getSubmitterName(selectedReceipt).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{getSubmitterName(selectedReceipt)}</p>
                          {selectedReceipt.createdBy && (
                            <p className="text-xs text-gray-500">{selectedReceipt.createdBy}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {selectedReceipt.gameId && (
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Related Game</p>
                        <p className="font-medium text-gray-900">{getGameName(selectedReceipt.gameId)}</p>
                      </div>
                    )}

                    <div>
                      <p className="text-sm text-gray-500 mb-1">Description</p>
                      <p className="text-gray-900">{selectedReceipt.description || 'No description provided'}</p>
                    </div>

                    {selectedReceipt.splitAmong && selectedReceipt.splitAmong.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Split Among</p>
                        <p className="text-gray-900">{selectedReceipt.splitAmong.length} player{selectedReceipt.splitAmong.length !== 1 ? 's' : ''}</p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mt-6 pt-6 border-t flex gap-3">
                    {selectedReceipt.receiptUrl ? (
                      <a
                        href={selectedReceipt.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700"
                      >
                        <Download size={18} />
                        Download Receipt
                      </a>
                    ) : (
                      <div className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-lg font-semibold">
                        <Receipt size={18} />
                        No Receipt Available
                      </div>
                    )}
                    <button
                      onClick={() => setSelectedReceipt(null)}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptManagement;